// @ts-nocheck
// #region ==================== IMPORTS ====================
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Divider,
  Autocomplete,
  Stack,
  CircularProgress,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AtomButton from '@/components/atoms/Button';
import AtomCustomCard from '@/components/atoms/CustomCard';
import FloatingActionButton from '@/components/atoms/FloatingActionButton';
import AtomTextField from '@/components/atoms/TextField';
import OrgDialog from '@/components/organisms/Dialog';
import RawTemplate from '@/components/templates/RawTemplate';
import { useSafeEffect } from '@/hooks/useSafeEffect';
import { AppDispatch } from '@/store';
import { selectUser } from '@/store/auth';
import {
  selectCompaniesList,
  selectCompaniesHasMore,
  selectCompaniesOffset,
  selectCompaniesLimit,
  selectIsLoadingMore,
  selectCompaniesLoading,
  selectTotalCompanies,
} from '@/store/companies/selectors';
import { setCurrentCompany, resetPagination } from '@/store/companies/slice';
import { companies } from '@/store/companies/thunks';
import { CompanySchema, CompanyCreateSchema } from '@/types/companies';
import styles from './supplier.module.css';
// #endregion

const NumberRangeFilter = ({ label, value, onChange, style }) => {
  const operators = [
    { value: 'eq', label: 'Equal to' },
    { value: 'gt', label: 'Greater than' },
    { value: 'lt', label: 'Less than' },
  ];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', ...style }}>
      <FormControl size="small" style={{ minWidth: 120 }} className={styles.filterField}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value.operator}
          onChange={e => onChange({ ...value, operator: e.target.value })}
          label={label}
        >
          {operators.map(op => (
            <MenuItem key={op.value} value={op.value}>
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <AtomTextField
        type="number"
        value={value.value}
        onChange={e => onChange({ ...value, value: e.target.value })}
        placeholder="Enter number"
        size="small"
        style={{ width: 120 }}
        className={styles.filterField}
        InputProps={{
          inputProps: { min: 0 },
        }}
      />
    </div>
  );
};

const Suppliers: React.FC = () => {
  // #region ==================== DISPATCH ====================
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const observerRef = useRef<IntersectionObserver | null>(null);
  // #endregion

  // #region ==================== STATES ====================
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isAddCompanyDialogOpen, setIsAddCompanyDialogOpen] = useState(false);
  const [newCompanyForm, setNewCompanyForm] = useState({
    name: '',
    description: '',
  });
  const [companyFormErrors, setCompanyFormErrors] = useState<Record<string, string>>({});
  const [filters, setFilters] = useState({
    name__like: '',
    name__startswith: '',
    region_headquartered__eq: '',
    currency__eq: '',
    study_id__eq: '',
    studiesFilter: {
      operator: 'eq',
      value: '',
    },
  });
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const latestFiltersRef = useRef(filters);
  const timeoutRef = useRef(null);
  const [orderBy, setOrderBy] = useState('');
  const [selectedAlphabet, setSelectedAlphabet] = useState('');
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const orderByOptions = [
    { value: '', label: 'Default Order' },
    { value: 'latest_revenue__desc', label: 'Latest Revenue (High to Low)' },
    { value: 'latest_revenue__asc', label: 'Latest Revenue (Low to High)' },
    { value: 'number_of_studies__desc', label: 'Number of Studies (High to Low)' },
    { value: 'number_of_studies__asc', label: 'Number of Studies (Low to High)' },
    { value: 'region__desc', label: 'Region (Z to A)' },
    { value: 'region__asc', label: 'Region (A to Z)' },
    { value: 'latest_financials_revenue__desc', label: 'Latest Financials Revenue (High to Low)' },
    { value: 'latest_financials_revenue__asc', label: 'Latest Financials Revenue (Low to High)' },
  ];
  const alphabetOptions = [
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
    'Z',
  ];
  // #endregion

  // #region ================== SELECTORS ================
  const reduxLoading = useSelector(selectCompaniesLoading);
  const companiesList = useSelector(selectCompaniesList);
  const hasMore = useSelector(selectCompaniesHasMore);
  const offset = useSelector(selectCompaniesOffset);
  const isLoadingMore = useSelector(selectIsLoadingMore);
  const limit = useSelector(selectCompaniesLimit);
  const useUser = useSelector(selectUser);
  const totalCompanies = useSelector(selectTotalCompanies);
  // #endregion

  // #region ================== API CALLS ================
  const fetchCompanies = useCallback(
    (isLoadMore = false, customFilters = null) => {
      // Get current filters from state directly instead of depending on them
      const filtersToUse = customFilters || filters;

      if (isLoadMore && !hasMore) return;
      if (isLoadMore && isLoadingMore) return;

      if (!isLoadMore) {
        dispatch(resetPagination());
      }

      const currentOffset = isLoadMore ? offset : 0;

      const apiParams: any = {
        offset: currentOffset,
        limit: limit,
      };

      if (orderBy) {
        apiParams.order_by = orderBy;
      }

      // Handle filter parameters properly
      Object.entries(filtersToUse).forEach(([key, value]) => {
        if (key === 'studiesFilter') {
          if (value.value && value.value.toString().trim() !== '') {
            const filterKey = `number_of_studies__${value.operator}`;
            apiParams[filterKey] = Number(value.value);
          }
        } else if (key.includes('__in')) {
          if (Array.isArray(value) && value.length > 0) {
            apiParams[key] = value;
          }
        } else {
          if (value && value.toString().trim() !== '') {
            apiParams[key] = value;
          }
        }
      });

      console.log('Fetching companies with params:', apiParams);

      try {
        dispatch(companies.getCompanies(apiParams));
      } catch (error) {
        console.error('Error fetching companies:', error);
      } finally {
        if (!isLoadMore) {
          setIsInitialLoad(false);
        }
      }
    },
    [dispatch, hasMore, limit, offset, isLoadingMore] // REMOVE 'filters' from here!
  );
  // #endregion

  // #region ================== EFFECTS ================
  useSafeEffect(() => {
    fetchCompanies(false);
  }, [dispatch]);

  // Update ref whenever filters change
  useEffect(() => {
    latestFiltersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollToTop(scrollTop > 300); // Show button after scrolling 300px
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Separate effect for API calls
  useEffect(() => {
    console.log('🔍 Filter effect triggered for:', filters.name__like);

    // Clear existing timeout
    if (timeoutRef.current) {
      console.log('🧹 Clearing previous timeout');
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      console.log('⏰ Making API call for:', latestFiltersRef.current.name__like);
      console.log('Latest filters:', latestFiltersRef.current);

      // Use the latest filters from ref
      const apiParams = {
        offset: 0,
        limit: limit,
      };

      if (orderBy) {
        apiParams.order_by = orderBy;
      }

      Object.entries(latestFiltersRef.current).forEach(([key, value]) => {
        if (key === 'studiesFilter') {
          if (value.value && value.value.toString().trim() !== '') {
            const filterKey = `number_of_studies__${value.operator}`;
            apiParams[filterKey] = Number(value.value);
          }
        } else if (key.includes('__in')) {
          if (Array.isArray(value) && value.length > 0) {
            apiParams[key] = value;
          }
        } else {
          if (value && value.toString().trim() !== '') {
            apiParams[key] = value;
          }
        }
      });

      console.log('API params:', apiParams);
      dispatch(resetPagination());
      dispatch(companies.getCompanies(apiParams));

      timeoutRef.current = null;
    }, 500);

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [filters, dispatch, limit, orderBy]);
  // #endregion

  // #region ================== FUNCTIONS ================

  // -------------------- SCROLL TO TOP --------------------
  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // -------------------- FILTER HANDLERS --------------------
  const handleFilterChange = (field: string, value: any) => {
    console.log('🔧 Filter changing:', field, '=', value);
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [field]: value,
      };
      console.log('🔧 New filters state:', newFilters);
      return newFilters;
    });
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      name__like: '',
      name__startswith: '',
      region_headquartered__eq: '',
      currency__eq: '',
      study_id__eq: '',
      studiesFilter: {
        operator: 'eq',
        value: '',
      },
    };

    setFilters(clearedFilters);
    setSelectedAlphabet('');
    setOrderBy('');

    // Force immediate fetch with cleared filters
    setTimeout(() => {
      fetchCompanies(false);
    }, 0);

    console.log('Clearing filters and fetching...');
  };

  const countActiveFilters = () => {
    const activeCount = Object.entries(filters).reduce((count, [key, value]) => {
      if (key === 'studiesFilter') {
        return count + (value.value && value.value.toString().trim() !== '' ? 1 : 0);
      }
      return count + (value && value.toString().trim() !== '' ? 1 : 0);
    }, 0);
    setActiveFiltersCount(activeCount);
  };

  const handleAlphabetClick = (letter: string) => {
    const newLetter = selectedAlphabet === letter ? '' : letter; // Toggle if same letter clicked
    setSelectedAlphabet(newLetter);

    // Update the filter
    handleFilterChange('name__startswith', newLetter);

    // Clear the name__like filter when using alphabet filter
    if (newLetter && filters.name__like) {
      handleFilterChange('name__like', '');
    }
  };

  // -------------------- COMPANY DETAILS --------------------
  const handleCompanyClick = (company: CompanySchema) => {
    // Navigate to company details page
    console.log('Company clicked:', company);
    dispatch(setCurrentCompany(company));
    navigate(`/companies/${company.name}`);
  };

  // -------------------- ADD COMPANY --------------------
  const handleAddCompanyDialogOpen = () => {
    setIsAddCompanyDialogOpen(true);
    setCompanyFormErrors({});
  };

  const handleAddCompanyDialogClose = () => {
    setIsAddCompanyDialogOpen(false);
    setNewCompanyForm({
      name: '',
      description: '',
    });
    setCompanyFormErrors({});
  };

  const handleCompanyFormChange = (field: string, value: any) => {
    setNewCompanyForm(prev => ({
      ...prev,
      [field]: value,
    }));

    if (companyFormErrors[field]) {
      setCompanyFormErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateCompanyForm = () => {
    const errors: Record<string, string> = {};

    if (!newCompanyForm.name.trim()) {
      errors.name = 'Company name is required';
    }

    setCompanyFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddCompanySubmit = async () => {
    if (!validateCompanyForm()) return;

    try {
      setLoading(true);

      const companyData: CompanyCreateSchema = {
        name: newCompanyForm.name,
        user_id: useUser.id,
        description: newCompanyForm.description || '',
      };

      await dispatch(companies.createCompany(companyData));

      fetchCompanies(false);
      handleAddCompanyDialogClose();
    } catch (error) {
      console.error('Error adding company:', error);
    } finally {
      setLoading(false);
    }
  };
  // #endregion

  // #region ================== MEMO ================
  useMemo(() => {
    countActiveFilters();
  }, [filters]);

  const sortedAndFilteredCompanies = useMemo(() => {
    console.log('Companies:', companiesList);

    if (!Array.isArray(companiesList)) {
      return [];
    }
    return [...companiesList];
  }, [companiesList]);

  const filterOptions = useMemo(() => {
    if (!Array.isArray(companiesList)) {
      return { regions: [], currencies: [] };
    }
    const regions = [...new Set(companiesList.map(c => c.region_headquartered).filter(Boolean))];
    const currencies = [...new Set(companiesList.map(c => c.currency).filter(Boolean))];

    return {
      regions: regions.map(region => ({ label: region, value: region })),
      currencies: currencies.map(currency => ({ label: currency, value: currency })),
    };
  }, [companiesList]);
  // #endregion

  // #region ================== CALLBACKS ================
  const loadMoreCompanies = useCallback(() => {
    console.log(
      'Load more triggered. hasMore:',
      hasMore,
      'isLoadingMore:',
      isLoadingMore,
      'loading:',
      loading
    );

    if (hasMore && !isLoadingMore && !loading) {
      console.log('Loading more companies...');
      fetchCompanies(true);
    }
  }, [hasMore, isLoadingMore, loading, fetchCompanies]);

  const lastCompanyRef = useCallback(
    (node: HTMLDivElement) => {
      if (loading || isLoadingMore) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
            console.log('Intersection detected, loading more...');
            loadMoreCompanies();
          }
        },
        {
          threshold: 0.1,
          rootMargin: '20px',
        }
      );

      if (node) observerRef.current.observe(node);
    },
    [loading, isLoadingMore, hasMore, loadMoreCompanies]
  );
  // #endregion

  return (
    <RawTemplate>
      <div className={styles.pageHeader}>
        <h1>Suppliers</h1>
        <div className={styles.headerActions}>
          <AtomButton
            variant="contained"
            color="primary"
            onClick={handleAddCompanyDialogOpen}
            size="medium"
          >
            Add Company
          </AtomButton>
        </div>
      </div>

      {/* Results Info */}
      <div className={styles.resultsInfo}>
        <div className={styles.searchSection}>
          <AtomTextField
            label="Company Name"
            value={filters.name__like}
            onChange={e => {
              handleFilterChange('name__like', e.target.value);

              // Clear alphabet filter when typing in search
              if (e.target.value && selectedAlphabet) {
                setSelectedAlphabet('');
                handleFilterChange('name__startswith', '');
              }

              console.log('Search changed to:', e.target.value);
            }}
            placeholder="Search companies by name..."
            size="medium"
            style={{ minWidth: 300, flex: 6 }}
            InputProps={{
              endAdornment: filters.name__like && (
                <IconButton
                  size="small"
                  onClick={() => handleFilterChange('name__like', '')}
                  edge="end"
                >
                  <ClearIcon />
                </IconButton>
              ),
            }}
          />

          {/* Order By Dropdown */}
          <FormControl size="medium" style={{ minWidth: 250, marginLeft: '12px', flex: 1 }}>
            <InputLabel>Order By</InputLabel>
            <Select value={orderBy} onChange={e => setOrderBy(e.target.value)} label="Order By">
              {orderByOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Filter Toggle Button */}
          <AtomButton
            variant="outlined"
            color="primary"
            onClick={() => setShowFilters(!showFilters)}
            size="large"
            style={{ marginLeft: '12px', padding: '0.944vw' }}
            endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            More Filters {activeFiltersCount > 1 && `(${activeFiltersCount - 1})`}
          </AtomButton>
        </div>

        {/* Collapsible Additional Filters Section */}
        <Collapse in={showFilters}>
          <div className={styles.filterSection}>
            <div className={styles.filterRow}>
              {/* Region Filter */}
              <Autocomplete
                options={filterOptions.regions}
                value={
                  filterOptions.regions.find(r => r.value === filters.region_headquartered__eq) ||
                  null
                }
                onChange={(event, newValue) =>
                  handleFilterChange('region_headquartered__eq', newValue?.value || '')
                }
                renderInput={params => (
                  <AtomTextField
                    {...params}
                    label="Region"
                    placeholder="Select region"
                    size="small"
                  />
                )}
                style={{ minWidth: 200 }}
                className={styles.filterField}
              />

              {/* Currency Filter */}
              <Autocomplete
                options={filterOptions.currencies}
                value={filterOptions.currencies.find(c => c.value === filters.currency__eq) || null}
                onChange={(event, newValue) =>
                  handleFilterChange('currency__eq', newValue?.value || '')
                }
                renderInput={params => (
                  <AtomTextField
                    {...params}
                    label="Currency"
                    placeholder="Select currency"
                    size="small"
                  />
                )}
                className={styles.filterField}
                style={{ minWidth: 150 }}
              />

              {/* Number Range Filter Component */}
              <NumberRangeFilter
                label="Number of Studies"
                value={filters.studiesFilter}
                onChange={newValue => handleFilterChange('studiesFilter', newValue)}
                style={{ minWidth: 250, flex: 2 }}
              />

              {/* Filter Actions */}
              <div className={styles.filterActions}>
                <AtomButton
                  variant="outlined"
                  size="small"
                  onClick={handleClearFilters}
                  disabled={activeFiltersCount === 0 || reduxLoading}
                >
                  Clear All
                </AtomButton>
              </div>

              {/* Alphabet Filter Panel */}
              <div className={styles.alphabetSection} style={{ marginTop: '16px' }}>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}
                >
                  <span style={{ fontWeight: 'bold', marginRight: '8px' }}>
                    Filter by First Letter:
                  </span>

                  {/* All button */}
                  <AtomButton
                    variant={!selectedAlphabet ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => handleAlphabetClick('')}
                    style={{
                      minWidth: '40px',
                      height: '32px',
                      fontSize: '12px',
                    }}
                  >
                    All
                  </AtomButton>

                  {/* Alphabet buttons */}
                  {alphabetOptions.map(letter => (
                    <AtomButton
                      key={letter}
                      variant={selectedAlphabet === letter ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => handleAlphabetClick(letter)}
                      style={{
                        minWidth: '32px',
                        height: '32px',
                        fontSize: '12px',
                        padding: '4px',
                      }}
                    >
                      {letter}
                    </AtomButton>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Collapse>
      </div>

      <p className={styles.result}>
        {filters.name__like && <span>Search results for "{filters.name__like}" - </span>}
        Showing {sortedAndFilteredCompanies.length} out of {totalCompanies} companies
      </p>

      <div className={styles.container}>
        {sortedAndFilteredCompanies.length > 0 ? (
          <>
            {sortedAndFilteredCompanies.map((company: CompanySchema, index) => (
              <AtomCustomCard
                key={company.id}
                ref={index === sortedAndFilteredCompanies.length - 1 ? lastCompanyRef : null}
                className={styles.cards}
                onClick={() => {
                  handleCompanyClick(company);
                }}
              >
                <div className={styles.content}>
                  <div className={styles.header}>
                    <h1 className={styles.percentage}>
                      {company.name || company.company_name || 'Unnamed Company'}
                    </h1>
                    <div className={styles.Divider}>
                      <Divider />
                    </div>
                    <div>
                      {/* <div className={styles.types}>
                        <h3 className={styles.typesTitle}>Company ID</h3>
                        <h3 className={styles.typesData}>{company.id}</h3>
                      </div> */}

                      {company.region_headquartered && (
                        <div className={styles.types}>
                          <h3 className={styles.typesTitle}>Region</h3>
                          <h3 className={styles.typesData}>{company.region_headquartered}</h3>
                        </div>
                      )}

                      {company.currency && (
                        <div className={styles.types}>
                          <h3 className={styles.typesTitle}>Currency</h3>
                          <h3 className={styles.typesData}>{company.currency}</h3>
                        </div>
                      )}

                      {company.number_of_studies !== undefined && (
                        <div className={styles.types}>
                          <h3 className={styles.typesTitle}>Number of Studies</h3>
                          <h3 className={styles.typesData}>{company.number_of_studies}</h3>
                        </div>
                      )}

                      {company?.latest_revenue !== undefined && (
                        <div className={styles.types}>
                          <h3 className={styles.typesTitle}>Latest Revenue</h3>
                          <h3 className={styles.typesData}>
                            {company.latest_revenue !== null && company.latest_revenue !== undefined
                              ? Number(company.latest_revenue).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                              : "NA"}
                          </h3>
                        </div>
                      )}

                      {company?.latest_financials_revenue !== undefined && (
                        <div className={styles.types}>
                          <h3 className={styles.typesTitle}>Latest Financials Revenue</h3>
                          <h3 className={styles.typesData}>
                            {company.latest_financials_revenue !== null && company.latest_financials_revenue !== undefined 
                            ? Number(company.latest_financials_revenue).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                            : "NA"}
                          </h3>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </AtomCustomCard>
            ))}

            {/* Loading indicator for more data */}
            {isLoadingMore && (
              <div className={styles.loadingMore}>
                <CircularProgress size={24} />
                <span>Loading more companies...</span>
              </div>
            )}

            {/* End of data indicator */}
            {!hasMore && !isInitialLoad && (
              <div className={styles.endOfData}>
                <p>You've reached the end of the list</p>
              </div>
            )}
          </>
        ) : (
          <div className={styles.noResults}>
            <p>No companies found matching your criteria.</p>
            {activeFiltersCount > 0 && (
              <AtomButton variant="outlined" onClick={handleClearFilters}>
                Clear All Filters
              </AtomButton>
            )}
          </div>
        )}
      </div>

      {/* Add Company Dialog */}
      <OrgDialog
        open={isAddCompanyDialogOpen}
        onClose={handleAddCompanyDialogClose}
        title="Add New Company"
        maxWidth="md"
        actions={
          <Stack direction="row" spacing={2}>
            <AtomButton variant="outlined" onClick={handleAddCompanyDialogClose} disabled={loading}>
              Cancel
            </AtomButton>
            <AtomButton variant="contained" onClick={handleAddCompanySubmit} loading={loading}>
              Add Company
            </AtomButton>
          </Stack>
        }
      >
        <div className={styles.addStudyForm}>
          <div className={styles.formRow}>
            <AtomTextField
              label="Company Name"
              value={newCompanyForm.name}
              onChange={e => handleCompanyFormChange('name', e.target.value)}
              error={!!companyFormErrors.name}
              helperText={companyFormErrors.name}
              required
              placeholder="Enter company name"
            />
          </div>

          <div className={styles.formRow}>
            <AtomTextField
              label="Description"
              multiline
              rows={3}
              value={newCompanyForm.description}
              onChange={e => handleCompanyFormChange('description', e.target.value)}
              placeholder="Enter company description (optional)"
            />
          </div>
        </div>
      </OrgDialog>

      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <FloatingActionButton
          icon={<KeyboardArrowUpIcon />}
          tooltip="Back to top"
          position="bottomRight"
          color="primary"
          size="medium"
          onClick={handleScrollToTop}
        />
      )}
    </RawTemplate>
  );
};

export default Suppliers;
